/**
 * progression.js - GAME-LEVEL-ENGINE progression rules
 *
 * Implements:
 *   section 8.4  success-rate target and the advance / support / step-back rule
 *   section 9.2  dynamic placement when a child starts a new game
 *   section 9.3  cross-game verification of generalization
 *
 * PURE. No state, no DOM, no database, no clock. Every function takes data and
 * returns a recommendation. Nothing here decides anything on the child's
 * behalf - "offer to advance" is an offer, and the caller presents it.
 */

import {
  SUPPORT_LADDER,
  getPhase,
  getLevel,
  getNextLevel,
  getPreviousLevel,
  getSupportLevel,
  nearestAvailableLevel,
  validateCoverage,
  compareLevels,
  levelIndex,
  COLORS,
} from './framework.js';

/* =========================================================================
 * SECTION 8.4 - the rule, verbatim
 *
 *   If success rate > 90% over 5 attempts:
 *       -> Offer to advance to next level
 *   If success rate < 70% over 3 attempts:
 *       -> Automatically increase prompting
 *       -> If still struggling, suggest previous level
 *
 * Read against a discrete window these are exact:
 *   5 attempts can only score 0/20/40/60/80/100%, so "> 90%" means 5 of 5.
 *   3 attempts can only score 0/33.3/66.7/100%, so "< 70%" means 2 of 3 or worse.
 *
 * "If still struggling" is the one part the spec leaves open. This engine reads
 * it as: a second sub-70% window after support has already been increased for
 * this level, or a sub-70% window when support is already at the phase's
 * most-supportive rung and cannot go higher. Both are recorded as
 * ENGINE DECISIONs, not spec text.
 * ========================================================================= */

export const RULES = {
  advance: {
    window: 5,
    threshold: 0.90,
    comparison: 'greater_than',
    requiredSuccesses: 5,
    cite: 'section 8.4',
    text: 'If success rate > 90% over 5 attempts: offer to advance to next level',
  },
  struggle: {
    window: 3,
    threshold: 0.70,
    comparison: 'less_than',
    maxSuccesses: 2,
    cite: 'section 8.4',
    text: 'If success rate < 70% over 3 attempts: automatically increase prompting; if still struggling, suggest previous level',
  },
  /*
   * PROMPT FADING - section 2.5, which section 8.4 does not implement.
   *
   * "Start with maximum support, systematically reduce as child succeeds:
   *  most-to-least prompting hierarchy; gradual fading, never sudden removal;
   *  child earns independence through demonstrated success."
   *
   * Errorless learning is raise-AND-fade. Section 8.4 gives the raise and is
   * silent on the fade, and the omission is not cosmetic: section 9.2 places
   * every child with EXTRA support scaled to their colour, and an unassessed
   * child is gray, which is the most extra support there is. Without a fade
   * rule that support is permanent, and the boxes that ask whether the child
   * produced something INDEPENDENTLY - Phase 2 C, Phase 6 D - can never be
   * true, so those levels can never go green, so the phase never completes.
   * A new child would be pinned to the bottom of the chart by the mechanism
   * that was supposed to protect them.
   *
   * THE RULE. Three consecutive countable attempts, all successful and none of
   * them needing help beyond the rung the trial opened at, fade the prompt by
   * ONE rung toward the level's target. Three consecutive independent correct
   * responses is the standard most-to-least criterion; one rung at a time is
   * section 2.5's "gradual fading, never sudden removal".
   *
   * ENGINE DECISION - two details the spec cannot settle:
   *
   * 1. "Needing help" is `unaided === false` on an attempt (support escalated
   *    within the trial, a hint fired, or the target was modelled before the
   *    child spoke). An attempt that does not say is treated as unaided. A
   *    game that cannot tell the difference should still fade on three
   *    consecutive successes - the classic criterion - because the alternative
   *    is the defect above.
   *
   * 2. A child meeting the ADVANCE threshold while still on extra support
   *    fades instead of advancing, whatever the unaided count. Succeeding five
   *    times out of five is itself the evidence that the scaffolding is doing
   *    work the child no longer needs, and advancing a level on scaffolding
   *    carries them up the chart on it - the level cannot be green anyway,
   *    because its independence boxes are false. Fade first, advance next time.
   */
  fade: {
    window: 3,
    requiredSuccesses: 3,
    cite: 'section 2.5',
    text: 'Systematically reduce support as the child succeeds: three consecutive unaided successes fade the prompt by one rung',
  },
  /*
   * MASTERY - section 7.5, which section 8.4 does not connect to.
   *
   * "The level's overall color is determined by the LOWEST box color. A level
   * is only Green when ALL boxes are Green."
   *
   * That is the definition of mastery in this framework, and until now nothing
   * in the pacing rules looked at it. Section 8.4's advance rule is a sliding
   * window over the last FIVE attempts, and the two are not the same question:
   *
   *   - A child can master a level - every scoreable box Green over ten plays -
   *     and still not have five consecutive successes in the most recent five,
   *     because three scattered misses over fifteen attempts is 80% and Green.
   *     The offer then never appears, and the child replays a level they have
   *     demonstrably finished.
   *   - Worse, the window OFFER is transient. It appears and disappears as the
   *     window slides, so whether a child advances depends on whether the offer
   *     happened to be standing at the moment their game asked. That is not a
   *     clinical decision, it is a race.
   *
   * A level that is Green is finished. The chart says so, in the terms the
   * whole chart exists to express, and it is a far stronger signal than five
   * recent attempts because it is ten first attempts per box with the
   * inconclusive ones already excluded.
   *
   * The action is still ADVANCE_OFFER, not a new one: the semantics are
   * identical - offer, never force (section 8.4) - and a game that already
   * handles the four actions needs no new vocabulary. `rule` and `mastered`
   * say why the offer was made.
   */
  mastery: {
    cite: 'section 7.5',
    text: 'A level whose every scoreable box is Green is mastered; offer the next one',
  },
  target: {
    min: 0.80,
    max: 0.90,
    cite: 'section 2.4 / 8.4',
    text: 'Maintain 80-90% success rate (errorless learning)',
  },
};

export const ACTIONS = {
  ADVANCE_OFFER: 'advance_offer',
  INCREASE_SUPPORT: 'increase_support',
  // Section 2.5. Automatic, like INCREASE_SUPPORT and for the same reason:
  // the prompt level is the engine's business, not something a child is asked
  // about. Only level changes are offered.
  REDUCE_SUPPORT: 'reduce_support',
  SUGGEST_PREVIOUS_LEVEL: 'suggest_previous_level',
  CONTINUE: 'continue',
  INSUFFICIENT_DATA: 'insufficient_data',
};

/**
 * Success rate over the most recent `window` attempts.
 *
 * @param {Array<boolean|{success:boolean, counted?:boolean}>} attempts
 *        chronological, OLDEST FIRST. Entries with `counted === false`
 *        (inconclusive recognition - see scoring.js) are dropped first, so a
 *        bad microphone never drags a child backwards.
 * @param {number} window how many recent attempts to consider
 * @returns {{rate:number|null, successes:number, size:number, full:boolean}}
 */
export function successRate(attempts = [], window) {
  const counted = attempts.filter(
    (a) => !(typeof a === 'object' && a !== null && a.counted === false)
  );
  const slice = counted.slice(-window);
  const successes = slice.filter((a) =>
    (typeof a === 'object' && a !== null ? Boolean(a.success) : Boolean(a))
  ).length;

  return {
    rate: slice.length ? successes / slice.length : null,
    successes,
    size: slice.length,
    full: slice.length >= window,
  };
}

/**
 * Is the child ready to have the prompt faded?
 *
 * The most recent `window` countable attempts, all successful, none of them
 * having needed help beyond the rung the trial opened at.
 *
 * @param {Array<boolean|{success:boolean, counted?:boolean, unaided?:boolean}>} attempts
 *        oldest first. `unaided === false` means the child needed help within
 *        the trial - support was escalated, a hint fired, or the target was
 *        modelled before they spoke. Absent means unknown, and is treated as
 *        unaided; see RULES.fade.
 * @param {number} window
 * @returns {{ready:boolean, full:boolean, size:number, successes:number, aided:number}}
 */
export function fadeReadiness(attempts = [], window) {
  const counted = attempts.filter(
    (a) => !(typeof a === 'object' && a !== null && a.counted === false)
  );
  const slice = counted.slice(-window);
  const successes = slice.filter((a) =>
    (typeof a === 'object' && a !== null ? Boolean(a.success) : Boolean(a))
  ).length;
  const aided = slice.filter(
    (a) => typeof a === 'object' && a !== null && a.unaided === false
  ).length;

  return {
    ready: slice.length >= window && successes === slice.length && aided === 0,
    full: slice.length >= window,
    size: slice.length,
    successes,
    aided,
  };
}

/**
 * Apply section 8.4 to a child's recent attempts at one level.
 *
 * @param {object} input
 * @param {number} input.phase
 * @param {number} input.level
 * @param {Array<boolean|{success:boolean, counted?:boolean}>} input.attempts
 *        whole-attempt outcomes at THIS level, oldest first. An attempt
 *        succeeds when the child completed the step, not per-box - the boxes
 *        drive colour (scoring.js), this drives pacing.
 * @param {number} [input.supportIndex] current rung on the SUPPORT_LADDER.
 *        Defaults to the phase baseline.
 * @param {number} [input.supportIncreases=0] how many times support has
 *        already been raised at this level in this session. Drives the
 *        "if still struggling" branch.
 * @param {object} [input.levelScore] the output of scoring.scoreLevel() for
 *        THIS level - the chart's own verdict. Supply it and a mastered level
 *        is offered onward whatever the last five attempts looked like; omit
 *        it and only the section 8.4 window applies, which is what every
 *        caller did before and is why a child could finish a level and be
 *        asked to play it again.
 * @param {number} [input.targetSupportIndex] the rung this LEVEL is designed
 *        to sit at - the ceiling for fading. Defaults to the phase baseline.
 *        Fading stops there: going further would quietly turn this level into
 *        a different one, and the framework has a mechanism for that already,
 *        called the next level.
 * @param {number[]} [input.availablePhases] the phases this game implements
 *        (section 8.7), so a step forward or back never lands off the game.
 * @param {Array<{phase:number, level:number}>} [input.availableLevels] the
 *        levels it implements within them, so a step forward or back never
 *        lands on a level the game does not have either.
 * @returns {{action:string, reason:string, rule:object|null,
 *            successRate:number|null, successes:number, sampleSize:number,
 *            phase:number, level:number,
 *            target:{phase:number, level:number}|null,
 *            support:{from:number, to:number, rung:object, changed:boolean},
 *            atCeiling:boolean, atFloor:boolean}}
 */
export function evaluateProgression({
  phase,
  level,
  attempts = [],
  supportIndex,
  supportIncreases = 0,
  targetSupportIndex,
  levelScore,
  availablePhases,
  availableLevels,
}) {
  const p = getPhase(phase);
  if (!p || !getLevel(phase, level)) {
    return {
      action: ACTIONS.INSUFFICIENT_DATA,
      reason: `No such level: phase ${phase}, level ${level}.`,
      rule: null,
      successRate: null,
      successes: 0,
      sampleSize: 0,
      phase,
      level,
      target: null,
      support: { from: null, to: null, rung: null, changed: false },
      atCeiling: false,
      atFloor: false,
      atTargetSupport: false,
      mastered: false,
      struggling: false,
    };
  }

  const bounds = p.support;
  const current = Number.isInteger(supportIndex) ? supportIndex : bounds.baseline;
  // Lower ladder index = MORE support. maxSupport is the most supportive rung
  // this phase may fall back to; minSupport is the least supportive it uses.
  const clamp = (i) => Math.max(bounds.maxSupport, Math.min(bounds.minSupport, i));
  const atFloor = current <= bounds.maxSupport;   // cannot add more support
  const atCeiling = current >= bounds.minSupport; // cannot remove more support

  // The rung this level is meant to sit at, and whether the child is carrying
  // extra support relative to it. Lower index = MORE support.
  const target = clamp(
    Number.isInteger(targetSupportIndex) ? targetSupportIndex : bounds.baseline
  );
  const hasExtraSupport = current < target;

  // Is the LEVEL finished (section 7.5)? Reported on every recommendation, not
  // only the one it drives: a mastered level where the child has just had a bad
  // patch still gets support first (section 2.4), and the caller should be able
  // to see both facts at once rather than inferring one from the action.
  const mastered = Boolean(levelScore && levelScore.mastered);

  const opts = {};
  if (availablePhases) opts.availablePhases = availablePhases;
  if (availableLevels) opts.availableLevels = availableLevels;
  const nextLevel = getNextLevel(phase, level, opts);
  const prevLevel = getPreviousLevel(phase, level, opts);

  const noChange = { from: current, to: current, rung: getSupportLevel(current), changed: false };

  const s = successRate(attempts, RULES.struggle.window);
  const a = successRate(attempts, RULES.advance.window);
  const struggling = s.full && s.rate < RULES.struggle.threshold;
  const advanceReady = a.full && a.rate > RULES.advance.threshold;

  /* ---- section 7.5: a finished level is finished -------------------------
   *
   * BEFORE the protective branch, and that ordering is a deliberate change
   * from "struggle always wins".
   *
   * Section 2.4 exists to protect a child who cannot do the thing: support
   * arrives before failure, and failure builds avoidance. A child whose every
   * scoreable box is Green has demonstrated nine or ten first-attempt
   * successes per box, with the inconclusive attempts already excluded. They
   * can do the thing. One miss inside a three-attempt window is noise, not a
   * struggle.
   *
   * And the cost of the other order is not theoretical. "< 70% over 3" means
   * two of three or worse, so a SINGLE miss trips it and it takes three clean
   * attempts to clear. A child holding the spec's own 80-90% target band trips
   * it about a tenth of the time and is then suppressed for three attempts -
   * so on a level of three or four trials, an ordinary child at a level they
   * have mastered may simply never be offered the next one. That is the first
   * playtest of Bubble Pop: fifteen attempts, both boxes Green, never advanced,
   * and it felt to the player like the same three scenes over and over.
   *
   * Raising support on a mastered level also frequently cannot do anything -
   * Phase 1 has exactly one rung - so the protection is nominal while the cost
   * is a child who cannot tell they are progressing.
   *
   * `struggling` is still reported, so a game can present the offer gently
   * rather than triumphantly. It is an OFFER; nothing is forced.
   */
  if (mastered && nextLevel) {
    return {
      action: ACTIONS.ADVANCE_OFFER,
      reason:
        `Every scoreable box in this level is Green (${levelScore.greenBoxes} of `
        + `${levelScore.scoredBoxes}), so the level is mastered under section 7.5. `
        + `Offer phase ${nextLevel.phase} level ${nextLevel.level}.`
        + (struggling
            ? ` The last ${s.size} attempts are below the 70% floor, so offer it gently -`
              + ' but a level this child has finished is finished.'
            : ''),
      rule: RULES.mastery,
      mastered: true,
      struggling,
      successRate: a.rate,
      successes: a.successes,
      sampleSize: a.size,
      phase,
      level,
      target: nextLevel,
      support: noChange,
      atCeiling,
      atFloor,
      atTargetSupport: !hasExtraSupport,
    };
  }

  // Section 2.4 requires support BEFORE failure, so the protective branch
  // beats everything below it. (Struggle and advance are in fact mutually
  // exclusive: 5 of 5 in the last five implies 3 of 3 in the last three.)
  if (struggling) {
    const stillStruggling = supportIncreases >= 1 || atFloor;

    if (stillStruggling) {
      return {
        action: prevLevel ? ACTIONS.SUGGEST_PREVIOUS_LEVEL : ACTIONS.INCREASE_SUPPORT,
        reason: prevLevel
          ? `${s.successes} of ${s.size} recent attempts succeeded (${pct(s.rate)}), below the 70% floor, ` +
            (atFloor
              ? `and support is already at the most supportive rung this phase allows ("${getSupportLevel(current).name}").`
              : `and prompting has already been increased ${supportIncreases} time(s) at this level.`) +
            ' Suggest stepping back a level rather than letting the child keep failing.'
          : `${s.successes} of ${s.size} recent attempts succeeded (${pct(s.rate)}), below the 70% floor, ` +
            'but this is the first level of the framework - there is nowhere to step back to. Hold at maximum support.',
        rule: RULES.struggle,
      mastered,
      struggling,
        successRate: s.rate,
        successes: s.successes,
        sampleSize: s.size,
        phase,
        level,
        target: prevLevel,
        support: { from: current, to: clamp(bounds.maxSupport), rung: getSupportLevel(clamp(bounds.maxSupport)), changed: clamp(bounds.maxSupport) !== current },
        atCeiling,
        atFloor,
        atTargetSupport: !hasExtraSupport,
      };
    }

    const raised = clamp(current - 1);
    return {
      action: ACTIONS.INCREASE_SUPPORT,
      reason:
        `${s.successes} of ${s.size} recent attempts succeeded (${pct(s.rate)}), below the 70% floor. ` +
        `Increase prompting from "${getSupportLevel(current).name}" to "${getSupportLevel(raised).name}".`,
      rule: RULES.struggle,
      mastered,
      struggling,
      successRate: s.rate,
      successes: s.successes,
      sampleSize: s.size,
      phase,
      level,
      target: null,
      support: { from: current, to: raised, rung: getSupportLevel(raised), changed: raised !== current },
      atCeiling,
      atFloor,
      atTargetSupport: !hasExtraSupport,
    };
  }

  // ---- section 2.5: fade the prompt ------------------------------------
  // See RULES.fade for why this branch exists and why it comes before the
  // advance branch.
  const f = fadeReadiness(attempts, RULES.fade.window);

  if (hasExtraSupport && (f.ready || advanceReady)) {
    const faded = Math.min(target, clamp(current + 1));
    return {
      action: ACTIONS.REDUCE_SUPPORT,
      reason: f.ready
        ? `${f.successes} consecutive unaided successes at "${getSupportLevel(current).name}". ` +
          `Fade to "${getSupportLevel(faded).name}"` +
          (faded === target
            ? ', which is where this level is meant to sit.'
            : `, one rung of ${target - current} toward "${getSupportLevel(target).name}".`)
        : `${a.successes} of ${a.size} recent attempts succeeded (${pct(a.rate)}) while still on more ` +
          `support than this level assumes. Fade from "${getSupportLevel(current).name}" to ` +
          `"${getSupportLevel(faded).name}" before offering the next level - a level cannot be ` +
          'mastered from a prompt below its own.',
      rule: RULES.fade,
      mastered,
      struggling,
      successRate: a.rate,
      successes: a.successes,
      sampleSize: a.size,
      phase,
      level,
      target: null,
      support: { from: current, to: faded, rung: getSupportLevel(faded), changed: faded !== current },
      atCeiling,
      atFloor,
      atTargetSupport: false,
    };
  }

  if (advanceReady) {
    return {
      action: nextLevel ? ACTIONS.ADVANCE_OFFER : ACTIONS.CONTINUE,
      reason: nextLevel
        ? `${a.successes} of ${a.size} recent attempts succeeded (${pct(a.rate)}), above the 90% mark. ` +
          `Offer phase ${nextLevel.phase} level ${nextLevel.level}.`
        : `${a.successes} of ${a.size} recent attempts succeeded (${pct(a.rate)}), but this is the last level available. Stay here.`,
      rule: RULES.advance,
      mastered,
      struggling,
      successRate: a.rate,
      successes: a.successes,
      sampleSize: a.size,
      phase,
      level,
      target: nextLevel,
      support: noChange,
      atCeiling,
      atFloor,
      atTargetSupport: !hasExtraSupport,
    };
  }

  if (!a.size) {
    return {
      action: ACTIONS.INSUFFICIENT_DATA,
      reason: 'No countable attempts yet at this level.',
      rule: null,
      mastered,
      struggling,
      successRate: null,
      successes: 0,
      sampleSize: 0,
      phase,
      level,
      target: null,
      support: noChange,
      atCeiling,
      atFloor,
      atTargetSupport: !hasExtraSupport,
    };
  }

  return {
    action: ACTIONS.CONTINUE,
    reason:
      `${a.successes} of ${a.size} recent attempts succeeded (${pct(a.rate)}). ` +
      'Inside the 80-90% target band or not enough data to act; keep playing at this level.',
    rule: RULES.target,
    mastered,
    struggling,
    successRate: a.rate,
    successes: a.successes,
    sampleSize: a.size,
    phase,
    level,
    target: null,
    support: noChange,
    atCeiling,
    atFloor,
    atTargetSupport: !hasExtraSupport,
  };
}

function pct(rate) {
  return rate === null ? 'n/a' : `${Math.round(rate * 100)}%`;
}

/* =========================================================================
 * SECTION 9.2 - DYNAMIC PLACEMENT
 *
 * "When a child starts a new game:
 *    1. System reads their current Phase/Level from the chart
 *    2. Places them at the equivalent Phase/Level in the new game
 *    3. Adjusts difficulty based on their overall color (not game-specific)"
 *
 * The spec's worked example: Phase 4 Level 3, Blue overall -> new game starts
 * at Phase 4 Level 3, "prompt level set for 'developing' (more support than if
 * Purple/Green)".
 *
 * COLOR_SUPPORT_OFFSET below turns that sentence into numbers. The direction
 * (weaker colour = more support) and Purple/Green sharing a level are from the
 * spec; the exact step counts are an ENGINE DEFAULT and are tunable.
 * ========================================================================= */

export const COLOR_SUPPORT_OFFSET = {
  green:  0, // least support - the child has demonstrated mastery
  purple: 0, // spec groups Purple with Green: "more support than if Purple/Green"
  blue:   1,
  yellow: 2,
  red:    3,
  gray:   2, // unassessed: start supportive, fade quickly (section 2.5)
};

/**
 * Place a child into a game they have not played (section 9.2).
 *
 * COVERAGE HAS TWO DIMENSIONS. `phasesAvailable` is section 8.7 and is what
 * the spec names. `levelsAvailable` is the other half: a game that implements
 * three of a phase's six levels is the normal case, not an exotic one, and
 * without it this function happily returns a level the game cannot load. Every
 * caller then re-clamps the answer, differently, and the chart records
 * whatever each of them decided. Declare it and the engine does it once.
 *
 * @param {object} input
 * @param {number} input.currentPhase from the child profile, not the game
 * @param {number} input.currentLevel
 * @param {string} [input.overallColor='gray'] the child's overall chart colour
 * @param {object} input.game
 * @param {string} input.game.id
 * @param {number[]} [input.game.phasesAvailable] which phases the game
 *        implements (section 8.7); games need not cover all ten
 * @param {Array<{phase:number, level:number}>} [input.game.levelsAvailable]
 *        which levels within them; omit for "every level of those phases"
 * @returns {{gameId:string, phase:number, level:number, supportIndex:number,
 *            supportRung:object, clamped:boolean, placement:string,
 *            reason:string, warning:string|null}}
 * @throws if the coverage declaration is malformed - a game that has declared
 *         its coverage wrongly must not be given a placement at all.
 */
export function placeInGame({ currentPhase, currentLevel, overallColor = 'gray', game }) {
  const coverage = validateCoverage({
    phasesAvailable: game && game.phasesAvailable,
    levelsAvailable: game && game.levelsAvailable,
  });
  if (!coverage.ok) {
    throw new Error(
      `Coverage declaration for game "${game && game.id}" is invalid:\n  - `
      + coverage.errors.join('\n  - ')
    );
  }

  const phases = coverage.availablePhases || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const opts = {};
  if (coverage.availablePhases) opts.availablePhases = coverage.availablePhases;
  if (coverage.availableLevels) opts.availableLevels = coverage.availableLevels;

  const minPhase = phases[0];
  const maxPhase = phases[phases.length - 1];

  let phase = currentPhase;
  let level = currentLevel;
  let clamped = false;
  let placement = 'exact';
  let warning = null;

  if (!phases.includes(currentPhase)) {
    clamped = true;
    if (currentPhase < minPhase) {
      phase = minPhase;
      level = 1;
      placement = 'above_child';
      warning =
        `${game.id} starts at phase ${minPhase}; the child is at phase ${currentPhase}. ` +
        'This game is developmentally ahead of them - expect a low success rate and consider a different game.';
    } else if (currentPhase > maxPhase) {
      phase = maxPhase;
      const p = getPhase(maxPhase);
      level = p.levels[p.levels.length - 1].level;
      placement = 'below_child';
      warning =
        `${game.id} stops at phase ${maxPhase}; the child is at phase ${currentPhase}. ` +
        'Placed at the top of this game, which is below their current skill.';
    } else {
      // Gap in the middle of the game's coverage: fall to the nearest phase below.
      phase = phases.filter((x) => x < currentPhase).pop();
      const p = getPhase(phase);
      level = p.levels[p.levels.length - 1].level;
      placement = 'gap_below';
      warning = `${game.id} does not implement phase ${currentPhase}; placed at the nearest phase below.`;
    }
  } else if (!getLevel(phase, level)) {
    clamped = true;
    placement = 'level_clamped';
    const p = getPhase(phase);
    level = Math.max(1, Math.min(p.levels.length, level));
    warning = `Level ${currentLevel} does not exist in phase ${phase}; clamped to level ${level}.`;
  }

  // ---- the level itself, against what the game implements ----------------
  // Downward, never upward - see framework.nearestAvailableLevel. The phase
  // clamp above may also have landed on a level this game does not have (the
  // last level of a phase it only partly implements), so this runs either way.
  const nearest = nearestAvailableLevel(phase, level, opts);
  if (!nearest) {
    throw new Error(
      `Game "${game.id}" declares coverage that contains no playable level near `
      + `phase ${phase} level ${level}.`
    );
  }
  if (nearest.clamped) {
    const asked = { phase, level };
    phase = nearest.phase;
    level = nearest.level;
    clamped = true;
    if (placement === 'exact') placement = 'level_not_implemented';
    const note =
      `${game.id} does not implement phase ${asked.phase} level ${asked.level}; placed at ` +
      `phase ${phase} level ${level}` +
      (nearest.direction === 'up'
        ? ', the nearest it has, which is ABOVE the child - there was nothing below.'
        : ', the nearest it has at or below that point.') +
      ' The chart still records against the level actually played.';
    warning = warning ? `${warning} ${note}` : note;
  }

  const p = getPhase(phase);
  const offset = COLOR_SUPPORT_OFFSET[overallColor] ?? COLOR_SUPPORT_OFFSET.gray;
  const supportIndex = Math.max(
    p.support.maxSupport,
    Math.min(p.support.minSupport, p.support.baseline - offset)
  );

  return {
    gameId: game && game.id,
    phase,
    level,
    supportIndex,
    supportRung: getSupportLevel(supportIndex),
    clamped,
    placement,
    reason:
      `Child is at phase ${currentPhase} level ${currentLevel}, overall ${(COLORS[overallColor] || COLORS.gray).name}. ` +
      `Placed at phase ${phase} level ${level} with "${getSupportLevel(supportIndex).name}" prompting ` +
      `(phase baseline "${getSupportLevel(p.support.baseline).name}" plus ${offset} step(s) of extra support). ` +
      `Fading returns them to the baseline as they succeed - see RULES.fade.`,
    warning,
  };
}

/* =========================================================================
 * SECTION 9.1 / 9.2 - WHERE THE CHILD IS
 *
 * "A child's position on the chart is GLOBAL, not per-game. Current phase is
 * stored at the child profile level. All games read from and write to the same
 * chart." (section 9.1)
 *
 * placeInGame() is the READ. This is the write, and without it §9.2 does not
 * work at all: a child who reaches Phase 3 in one game opens another and is
 * placed at Phase 1 Level 1, because that is what their profile still says.
 * Cross-game intelligence is the thing that makes the chart worth having, and
 * it is entirely downstream of this one pair of columns being correct.
 *
 * ---------------------------------------------------------------------------
 * DERIVED, NOT ASSERTED
 *
 * A game does not get to say where a child now is, for the same reason it does
 * not get to say which attempt this was or whether the child needed help. It
 * says what it just DID - an attempt, at a phase and a level - and the engine
 * decides what that means for the child's position.
 *
 * ---------------------------------------------------------------------------
 * ONE STEP, EITHER WAY, ALONG THE PLAYING GAME'S OWN PATH
 *
 * Forward: only to the level the engine itself would name as next for this
 * game (section 8.7 coverage included, so a game that implements 1.3 then 2.1
 * moves the child across that gap and one that does not, cannot). A game that
 * posts an attempt three levels ahead does not get to carry the child there.
 *
 * Backward: also one step, and only when the game could have stayed where the
 * child was. That distinction is what separates a REGRESSION from a CLAMP:
 *
 *   - A full-coverage game posting an attempt one level below the child's
 *     position is section 8.4's step back being taken. The position follows,
 *     because section 2.4 means the next game should meet them where they are
 *     rather than where they were.
 *   - A game that stops at Phase 3 posting Phase 3 attempts for a child at
 *     Phase 5 is placement clamping (placeInGame, `below_child`). It must NOT
 *     demote the child: an easier game is not a regression, and a platform
 *     where playing something simple costs a child their place is a platform
 *     nobody will let a child play twice.
 *
 * Any move of more than one step in either direction is refused and reported.
 * ========================================================================= */

/**
 * Where a child is after playing one attempt.
 *
 * @param {object} input
 * @param {{phase:number, level:number}} input.current   from the child profile
 * @param {{phase:number, level:number}} input.played    the attempt just recorded
 * @param {{phasesAvailable?:number[], levelsAvailable?:Array}} [input.coverage]
 *        what the PLAYING game implements. Without it, every level counts as
 *        reachable, which is right for a game that covers the framework.
 * @param {{phase:number, level:number}} [input.furthest] the high-water mark
 * @returns {{phase:number, level:number, moved:'up'|'down'|'none'|'refused',
 *            changed:boolean, furthest:{phase:number, level:number},
 *            furthestChanged:boolean, reason:string}}
 */
export function nextPosition({ current, played, coverage, furthest }) {
  const cov = validateCoverage(coverage);
  const opts = {};
  if (cov.availablePhases) opts.availablePhases = cov.availablePhases;
  if (cov.availableLevels) opts.availableLevels = cov.availableLevels;

  const playedIndex = played ? levelIndex(played.phase, played.level) : -1;
  const currentIndex = current ? levelIndex(current.phase, current.level) : -1;

  const settle = (position, moved, reason) => {
    const high = pickFurthest(furthest, position);
    return {
      phase: position.phase,
      level: position.level,
      moved,
      changed: currentIndex === -1 || levelIndex(position.phase, position.level) !== currentIndex,
      furthest: high.position,
      furthestChanged: high.changed,
      reason,
    };
  };

  if (playedIndex === -1) {
    return settle(current, 'refused', `Phase ${played && played.phase} level ${played && played.level} is not a level in the framework.`);
  }
  if (currentIndex === -1) {
    // A profile with no usable position - a new child, or a corrupt row. What
    // the child is actually playing is better evidence than nothing.
    return settle(played, 'up', 'The child had no valid position; adopting the level they are playing.');
  }
  if (playedIndex === currentIndex) {
    return settle(current, 'none', 'Still playing the level the child is on.');
  }

  if (playedIndex > currentIndex) {
    const next = getNextLevel(current.phase, current.level, opts);
    if (next && next.phase === played.phase && next.level === played.level) {
      return settle(played, 'up',
        `Advanced from phase ${current.phase} level ${current.level} to phase `
        + `${played.phase} level ${played.level}, which is the next level this game implements.`);
    }
    return settle(current, 'refused',
      `An attempt arrived at phase ${played.phase} level ${played.level}, which is more than one `
      + `step ahead of phase ${current.phase} level ${current.level}. A game cannot carry a child `
      + 'forward; the position holds and the attempt is still recorded where it happened.');
  }

  // Behind. Regression or clamp?
  const canReachCurrent = cov.ok
    ? (!cov.availablePhases && !cov.availableLevels)
      || Boolean(getLevel(current.phase, current.level)
        && (!cov.availablePhases || cov.availablePhases.includes(current.phase))
        && (!cov.availableLevels || cov.availableLevels.some(
          (l) => l.phase === current.phase && l.level === current.level)))
    : true;

  if (!canReachCurrent) {
    return settle(current, 'none',
      `This game does not implement phase ${current.phase} level ${current.level}, so an attempt `
      + `at phase ${played.phase} level ${played.level} is placement clamping down to what the game `
      + 'has, not the child stepping back. The position holds.');
  }

  const previous = getPreviousLevel(current.phase, current.level, opts);
  if (previous && previous.phase === played.phase && previous.level === played.level) {
    return settle(played, 'down',
      `Stepped back from phase ${current.phase} level ${current.level} to phase ${played.phase} `
      + `level ${played.level}. Section 2.4: the next game should meet the child where they are.`);
  }

  return settle(current, 'refused',
    `An attempt arrived at phase ${played.phase} level ${played.level}, more than one step behind `
    + `phase ${current.phase} level ${current.level}. The position holds.`);
}

/**
 * The further of two positions, in chart order. Never goes backwards.
 *
 * WHY A SEPARATE HIGH-WATER MARK. The current position moves both ways,
 * because that is what section 9.2 needs to place a child correctly and what
 * section 2.4 needs to stop meeting them above their level. But "how far has
 * this child got" is a different question with a different answer, and it is
 * the one a parent asks. Storing only the current position answers the first
 * and quietly loses the second: a child who has reached Phase 4 and stepped
 * back to Phase 3 has still reached Phase 4, and no report should say
 * otherwise.
 */
function pickFurthest(furthest, position) {
  const cmp = compareLevels(position, furthest);
  if (furthest && cmp !== null && cmp <= 0) {
    return { position: { phase: furthest.phase, level: furthest.level }, changed: false };
  }
  return { position: { phase: position.phase, level: position.level }, changed: true };
}

/* =========================================================================
 * SECTION 9.3 - CROSS-GAME VERIFICATION
 *
 * "True mastery is verified when the same level shows Green across 2+
 *  different games." This is the ABA generalization principle: proof the skill
 *  transferred, not just that it was drilled in one context.
 * ========================================================================= */

export const GENERALIZATION_GAMES_REQUIRED = 2;

/**
 * Has one level generalized across games?
 *
 * @param {object} input
 * @param {number} input.phase
 * @param {number} input.level
 * @param {Object<string,string>} input.perGame colour id per game id, e.g.
 *        { block_builder: 'green', kitchen_adventure: 'blue' }
 * @param {number} [input.required=2]
 * @returns {{phase:number, level:number, verified:boolean, greenGames:string[],
 *            gamesPlayed:string[], required:number, remaining:number,
 *            badge:string|null, reason:string}}
 */
export function checkGeneralization({ phase, level, perGame = {}, required = GENERALIZATION_GAMES_REQUIRED }) {
  const gamesPlayed = Object.keys(perGame);
  const greenGames = gamesPlayed.filter((g) => perGame[g] === 'green').sort();
  const verified = greenGames.length >= required;

  return {
    phase,
    level,
    verified,
    greenGames,
    gamesPlayed,
    required,
    remaining: Math.max(0, required - greenGames.length),
    badge: verified ? 'Generalization Verified' : null,
    reason: verified
      ? `Green in ${greenGames.length} games (${greenGames.join(', ')}). The skill has transferred across contexts.`
      : `Green in ${greenGames.length} of the ${required} games needed. ` +
        (greenGames.length
          ? `Green so far in: ${greenGames.join(', ')}. Play this level in another game to verify.`
          : 'Not yet Green in any game.'),
  };
}

/**
 * Run checkGeneralization over a whole chart.
 *
 * @param {Object<string, Object<string,string>>} byLevel keyed "phase.level",
 *        e.g. { "4.3": { block_builder: 'green', kitchen: 'green' } }
 * @param {number} [required=2]
 * @returns {{verified:Array, pending:Array, verifiedCount:number}}
 */
export function generalizationSummary(byLevel = {}, required = GENERALIZATION_GAMES_REQUIRED) {
  const results = Object.entries(byLevel).map(([key, perGame]) => {
    const [phase, level] = key.split('.').map(Number);
    return checkGeneralization({ phase, level, perGame, required });
  });

  return {
    verified: results.filter((r) => r.verified),
    pending: results.filter((r) => !r.verified && r.greenGames.length > 0),
    verifiedCount: results.filter((r) => r.verified).length,
  };
}

/**
 * Should a level be treated as truly mastered? Green alone is mastery within
 * one game; section 9.3 raises the bar to Green in two.
 *
 * @param {object} input
 * @param {string} input.color the level's colour in the current game
 * @param {object} [input.generalization] output of checkGeneralization()
 * @returns {{mastered:boolean, verified:boolean, level:string}}
 */
export function masteryStatus({ color, generalization }) {
  const mastered = color === 'green';
  const verified = Boolean(generalization && generalization.verified);
  return {
    mastered,
    verified,
    level: verified ? 'generalized' : mastered ? 'mastered' : 'in_progress',
  };
}

export default {
  RULES,
  ACTIONS,
  SUPPORT_LADDER,
  COLOR_SUPPORT_OFFSET,
  GENERALIZATION_GAMES_REQUIRED,
  successRate,
  fadeReadiness,
  evaluateProgression,
  nextPosition,
  placeInGame,
  checkGeneralization,
  generalizationSummary,
  masteryStatus,
};
